import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import StatusBadge from "../StatusBadge";
import { useReveal } from "../../lib/animations";

const sample = [
  { name: "Voorbereidende werken", price: "€ 1.240", market: "€ 1.150 – € 1.350", status: "EERLIJK", diff: "+1.2%" },
  { name: "Materiaal isolatie", price: "€ 3.480", market: "€ 2.700 – € 3.100", status: "HOOG", diff: "+18.5%" },
  { name: "Arbeidsuren", price: "€ 1.860", market: "€ 1.700 – € 2.000", status: "EERLIJK", diff: "+0.8%" },
  { name: "Afwerking", price: "€ 980", market: "€ 720 – € 880", status: "HOOG", diff: "+14.2%" },
  { name: "Overhead", price: "€ 740", market: "€ 380 – € 480", status: "OVERDREVEN", diff: "+72.6%" },
];

export default function ReportPreview() {
  const ref = useReveal();
  return (
    <section data-testid="report-preview" className="oc-section bg-[#003B82] text-white relative overflow-hidden">
      <div className="absolute inset-0 oc-grid-bg opacity-15" />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-oc-secondary/20 blur-3xl" />

      <div ref={ref} className="oc-reveal oc-container relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[13px] uppercase tracking-[0.1em] font-semibold text-oc-hover">
              Het rapport
            </span>
            <h2 className="font-display font-bold text-[32px] sm:text-[40px] lg:text-[48px] leading-tight tracking-tight mt-3 text-white">
              Zo ziet jouw rapport eruit
            </h2>
            <p className="mt-5 text-white/80 text-[16px] sm:text-[17px] leading-relaxed max-w-lg">
              Een gedetailleerde heatmap. Per post de marktprijs, het verschil én een concrete onderhandelingstip. Geen vakjargon — wel duidelijke acties.
            </p>
            <ul className="mt-8 space-y-3 text-white/85">
              <Bullet>Score van 0 tot 100 met directe duiding</Bullet>
              <Bullet>Marktprijs per lijnpost (min – max)</Bullet>
              <Bullet>Drie concrete onderhandelingstips</Bullet>
              <Bullet>PDF-rapport om te delen of te bewaren</Bullet>
            </ul>
            <Link to="/analyse" className="inline-block mt-10">
              <Button
                data-testid="report-preview-cta"
                className="group bg-white text-oc-primary hover:bg-white hover:scale-[1.02] transition-all rounded-oc-btn h-13 px-7 font-semibold"
              >
                Maak mijn eigen rapport
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </Link>
          </div>

          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-oc p-2 shadow-2xl">
            <div className="bg-white text-oc-textPrimary rounded-[16px] p-7">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[12px] uppercase tracking-[0.1em] font-semibold text-oc-textSecondary">Rapport voorbeeld</span>
                  <p className="font-display text-oc-primary font-bold text-[22px] leading-tight mt-1">Renovatie · Gent</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-[40px] font-semibold leading-none text-oc-high">71</p>
                  <span className="text-[11px] uppercase tracking-[0.1em] text-oc-textSecondary">van 100</span>
                </div>
              </div>
              <div className="mt-6 divide-y divide-oc-border border border-oc-border rounded-md overflow-hidden">
                {sample.map((row, i) => (
                  <div
                    key={i}
                    className="px-3 sm:px-4 py-3 flex items-center text-[12px] sm:text-[13px] gap-2"
                    style={{ animation: `fade-in 600ms ease-out both ${300 + i * 80}ms` }}
                  >
                    <p className="flex-1 font-medium text-oc-primary truncate">{row.name}</p>
                    <p className="font-mono text-right whitespace-nowrap text-oc-primary">{row.price}</p>
                    <StatusBadge status={row.status} compact />
                  </div>
                ))}
              </div>
              <p className="mt-5 text-[12px] text-oc-textSecondary">
                Dit is een voorbeeld. Jouw rapport is gebaseerd op jouw offerte.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-oc-hover shrink-0" />
      <span>{children}</span>
    </li>
  );
}
